import React, { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import Navbar from "./Navbar";
import Footer from "./Footer";

const PrivacyPolicy = () => {
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (sectionId) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const sections = [
    {
      id: "information-collect",
      title: "1. Information We Collect",
      content: [
        {
          subtitle: "a) Personal Information:",
          items: [
            "Name, email address, phone number, and delivery address.",
            "Payment information (processed securely by third-party payment processors).",
          ],
        },
        {
          subtitle: "b) Device and Usage Information:",
          items: [
            "Device type, operating system, and IP address.",
            "App usage data, including features used and time spent on the app.",
            "Location data (with your consent) to facilitate order deliveries.",
          ],
        },
        {
          subtitle: "c) Transaction and Order Details:",
          items: [
            "Items ordered, transaction history, and delivery preferences.",
          ],
        },
      ],
    },
    {
      id: "information-use",
      title: "2. How We Use Your Information",
      content: [
        {
          items: [
            "Provide and improve our services.",
            "Process and deliver orders efficiently.",
            "Communicate with you regarding your orders, updates, and promotions.",
            "Enhance security, prevent fraud, and comply with legal obligations.",
            "Improve user experience through analytics and personalization.",
          ],
        },
      ],
    },
    {
      id: "information-sharing",
      title: "3. Information Sharing and Disclosure",
      content: [
        {
          items: [
            "Delivery partners to fulfill your orders.",
            "Payment gateways for secure transactions.",
            "Third-party service providers for analytics, customer support, and marketing.",
            "Legal authorities when required by law or for fraud prevention.",
          ],
        },
      ],
    },
    {
      id: "data-security",
      title: "4. Data Security",
      content: [
        {
          items: [
            "We implement robust security measures to protect your data from unauthorized access, alteration, or disclosure.",
            "No system is completely secure, and we encourage users to keep their account credentials confidential.",
          ],
        },
      ],
    },
    {
      id: "user-rights",
      title: "5. Your Rights and Choices",
      content: [
        {
          items: [
            "Access, update, or delete your personal information.",
            "Opt-out of promotional communications.",
            "Control location and data-sharing settings within your device.",
          ],
        },
      ],
    },
    {
      id: "third-party",
      title: "6. Third-Party Links",
      content: [
        {
          items: [
            "TezChal may contain links to third-party websites or services.",
            "We are not responsible for their privacy practices, and we encourage users to review their policies before sharing information.",
          ],
        },
      ],
    },
    {
      id: "policy-changes",
      title: "7. Changes to This Privacy Policy",
      content: [
        {
          items: [
            "We may update this Privacy Policy from time to time.",
            "We will notify you of any significant changes through our app or other communication channels.",
          ],
        },
      ],
    },
  ];

  return (
    <>
      <Navbar />
      <div className="max-w-4xl mx-auto p-4 space-y-6">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">Privacy Policy</h1>
          {/* <p className="text-gray-600">Effective Date: [Insert Date]</p> */}
        </div>

        {/* Welcome Section */}
        <div className="bg-white rounded-lg shadow-md mb-6">
          <div className="p-6">
            <p className="text-lg">
              Welcome to <span className="font-bold">TezChal</span>! Your
              privacy is important to us. This Privacy Policy explains how{" "}
              <span className="font-bold">TezChal Private Limited</span>{" "}
              ("TezChal," "we," "our," or "us") collects, uses, and protects
              your personal information when you use our quick commerce mobile
              application and services.
            </p>
          </div>
        </div>

        {/* Collapsible Sections */}
        {sections.map((section) => (
          <div key={section.id} className="bg-white rounded-lg shadow-md mb-4">
            <div
              className="p-4 flex justify-between items-center cursor-pointer hover:bg-gray-50 rounded-t-lg"
              onClick={() => toggleSection(section.id)}
            >
              <h2 className="lg:text-xl xsm:text-[16.5px] font-semibold">{section.title}</h2>
              {expandedSections[section.id] ? (
                <ChevronUp className="w-6 h-6 text-gray-500" />
              ) : (
                <ChevronDown className="w-6 h-6 text-gray-500" />
              )}
            </div>

            {expandedSections[section.id] && (
              <div className="p-4 pt-0">
                {section.content.map((contentBlock, idx) => (
                  <div key={idx} className="mb-4">
                    {contentBlock.subtitle && (
                      <h3 className="font-semibold mb-2">
                        {contentBlock.subtitle}
                      </h3>
                    )}
                    <ul className="list-disc pl-6 space-y-2">
                      {contentBlock.items.map((item, itemIdx) => (
                        <li key={itemIdx} className="text-gray-700">
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}

        {/* Contact Section */}
        <div className="bg-white rounded-lg shadow-md mt-8">
          <div className="p-6">
            <h2 className="text-xl font-semibold mb-4">Contact Us</h2>
            <p className="mb-4">
              If you have any questions or concerns regarding this Privacy
              Policy, please contact us at:
            </p>
            <div className="space-y-2">
              <p className="font-bold">TezChal Private Limited</p>
              <p>
                {" "}
                Apartment Sandeep Vihar, Block/Tower G-25, <br/>Floor No. 13 and 14,
                Kannamangala Village <br/>Bidarahalli, Bengaluru East Taluk,
                Karnataka-560067
              </p>
              <p>info@tezchal.com</p>
              <p>+91 8981440034</p>
            </div>
          </div>
        </div>

        {/* Agreement Footer */}
        <p className="text-center text-gray-600 mt-8">
          By using TezChal, you agree to the terms outlined in this Privacy
          Policy.
        </p>
      </div>
      <Footer />
    </>
  );
};

export default PrivacyPolicy;
